import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import UnfoldMoreRounded from '@mui/icons-material/UnfoldMoreRounded';
import Avatar from '@components/Avatar';
import { Typography } from '@components/Typography';
import Notifications from '@features/Notifications';
import NotificationsBadge from '@features/Notifications/NotificationsBadge';
import { useTranslation } from '@hooks';
import { getInitialsParts, getFullName } from '@helpers/userName';
import { NotificationsLightIcon } from 'assets/images/icons';
import CollapsedMenuItem from './CollapsedMenuItem';
import { isNavigationItemActive } from './NavigationLink';
import ProfileMenu, { CollapsedProfileMenu, PROFILE_MENU_ID } from './ProfileMenu';
import { Footer, FooterRow, FooterCount, CollapsedBell } from './styles';

const formatCount = count => (count > 99 ? '99+' : count);

const SidebarFooter = ({ collapsed, activeBranch = true, items, accountSwitcher, onLogout, openMenu, setOpenMenu }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const user = useSelector(state => state.auth.user);
    const { pathname } = useLocation();
    const { t } = useTranslation();

    const fullName = getFullName(user?.firstName, user?.lastName);
    const isActive = activeBranch && items.some(item => isNavigationItemActive(item, pathname));
    const menuOpen = !!anchorEl;
    const avatar = (
        <Avatar
            size="medium"
            image={user?.profileImageUrl}
            fullName={fullName}
            label={getInitialsParts(user?.firstName, user?.lastName)}
            sx={{ border: 0 }}
        />
    );
    const rowProps = { items, accountSwitcher, onLogout };

    if (collapsed) {
        return (
            <Footer>
                <Notifications
                    trigger={({ onClick, totalCount }) => (
                        <CollapsedMenuItem
                            narrow
                            item={{
                                id: 'pl-menu-left-link-notifications',
                                title: t('common.notifications'),
                                ariaLabel: totalCount
                                    ? `${t('common.notifications')} ${formatCount(totalCount)}`
                                    : undefined,
                                badge: totalCount ? <FooterCount>{formatCount(totalCount)}</FooterCount> : null,
                                icon: (
                                    <CollapsedBell>
                                        <NotificationsLightIcon />
                                        <NotificationsBadge count={totalCount} />
                                    </CollapsedBell>
                                ),
                            }}
                            callback={onClick}
                            openMenu={openMenu}
                            setOpenMenu={setOpenMenu}
                        />
                    )}
                />
                <CollapsedMenuItem
                    item={{
                        id: 'pl-menu-left-link-profile',
                        title: t('Sidebar.accountMenu'),
                        icon: avatar,
                        renderMenu: menuProps => <CollapsedProfileMenu {...rowProps} {...menuProps} />,
                    }}
                    active={isActive}
                    openMenu={openMenu}
                    setOpenMenu={setOpenMenu}
                />
            </Footer>
        );
    }

    return (
        <Footer>
            <Notifications
                trigger={({ onClick, totalCount }) => (
                    <FooterRow type="button" id="pl-menu-left-link-notifications" onClick={onClick}>
                        <span className="sidebar-active-indicator" />
                        <NotificationsLightIcon />
                        <Typography variant="body1">{t('common.notifications')}</Typography>
                        {totalCount ? <FooterCount>{formatCount(totalCount)}</FooterCount> : null}
                    </FooterRow>
                )}
            />
            <FooterRow
                type="button"
                id="pl-menu-left-link-profile"
                isActive={isActive}
                aria-current={isActive ? 'page' : undefined}
                aria-label={t('Sidebar.accountMenu')}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-controls={menuOpen ? PROFILE_MENU_ID : undefined}
                onClick={event => setAnchorEl(event.currentTarget)}
            >
                <span className="sidebar-active-indicator" />
                {avatar}
                <Typography variant="body1" noWrap>
                    {fullName}
                </Typography>
                <UnfoldMoreRounded className="sidebar-footer-arrow" aria-hidden="true" />
            </FooterRow>
            <ProfileMenu anchorEl={anchorEl} onClose={() => setAnchorEl(null)} {...rowProps} />
        </Footer>
    );
};

export default SidebarFooter;
